/**
 * Inactivity lock (SEC-014, URS-11.7-007; 21 CFR 11.300(d), Annex 11 §12).
 *
 * Why the client has to do this: the app renews its five-minute access token in the background
 * (session-refresh.ts), and a renewal counts as activity at the identity provider, so the provider's own idle
 * timeout never fires while a tab is open. A shared gowned-area tablet left unlocked would let the next person's
 * entry be attributed to the signed-in user. After the configured quiet period (minutes on the shop floor, longer
 * in the office; both set by change-controlled policy, GET /api/v1/me/session-policy) the session is ended on
 * the server, token renewal stops, every record is removed from the screen and the lock screen shows none.
 *
 * Unlocking is a fresh authentication at the identity provider, never a shortcut: the lock flag survives a reload
 * (session storage), and while it is set every sign-in is forced to ask for credentials (auth.ts).
 */
export type WorkstationClass = "floor" | "office";

export interface SessionPolicy {
  lockSeconds: { floor: number; office: number };
  absoluteSeconds: number;
}

/** Used when the policy cannot be read: the shorter, safer interval, never "no lock". */
export const FALLBACK_POLICY: SessionPolicy = {
  lockSeconds: { floor: 300, office: 900 },
  absoluteSeconds: 43_200,
};

const CLASS_KEY = "ebr.workstation-class";
const LOCK_KEY = "ebr.locked";
export const CLASS_CHANGED = "ebr:workstation-class-changed";
/** Activity is sampled at most this often, so a stream of mouse moves does not churn timers. */
export const ACTIVITY_SAMPLE_MS = 1000;

type Reader = Pick<Storage, "getItem">;
type Writer = Pick<Storage, "setItem" | "removeItem">;

/** An unconfigured device is a shop-floor device: the stricter interval is the default, not the exception. */
export function workstationClass(storage: Reader): WorkstationClass {
  return storage.getItem(CLASS_KEY) === "office" ? "office" : "floor";
}

export function setWorkstationClass(storage: Writer, c: WorkstationClass): void {
  storage.setItem(CLASS_KEY, c);
}

export function lockMs(policy: SessionPolicy, c: WorkstationClass): number {
  return policy.lockSeconds[c] * 1000;
}

export const isLocked = (storage: Reader): boolean => storage.getItem(LOCK_KEY) === "1";
export const markLocked = (storage: Writer): void => storage.setItem(LOCK_KEY, "1");
export const clearLocked = (storage: Writer): void => storage.removeItem(LOCK_KEY);

export interface IdleDeps {
  idleMs: number;
  onLock: () => void;
  now: () => number;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

/** `touch()` on every user action; `onLock` fires once after `idleMs` without one. */
export function createIdleLock(deps: IdleDeps) {
  let handle: unknown = null;
  let lastReset = Number.NEGATIVE_INFINITY;
  let done = false;

  const arm = (): void => {
    if (handle !== null) deps.clearTimer(handle);
    lastReset = deps.now();
    handle = deps.setTimer(() => {
      handle = null;
      done = true;
      deps.onLock();
    }, deps.idleMs);
  };

  arm();
  return {
    touch: (): void => {
      if (!done && deps.now() - lastReset >= ACTIVITY_SAMPLE_MS) arm();
    },
    stop: (): void => {
      done = true;
      if (handle !== null) deps.clearTimer(handle);
      handle = null;
    },
  };
}
